import { computed, inject } from "@angular/core";
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { setAllEntities, setEntity, withEntities } from "@ngrx/signals/entities";
import { UserFacade } from "../user/user.facade";
import { extractErrorMessage } from "../../interceptors/helpers/extract-error-message.helper";
import { exhaustMap, firstValueFrom, map, pipe, tap } from "rxjs";
import { FeeScheduleService } from "../../services/fee-schedule/fee-schedule.service";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tapResponse } from "@ngrx/operators";

export interface FeeSchedule {
  id: number;
    provider_id: number;
    clinic_id: number;
  discount_percent: number;

  custom_set_discount: number;
  custom_daily_visit: number;
  custom_new_visit: number;
  custom_routine_visit: number;

  daily_visit: number;
  new_visit: number;
  routine_visit: number;

  set_daily_max: number;

  excluded_services: {
    set: string;
    custom: string;
  };

  cpt_rates: FeeScheduleCptRate[];
  cpt_discounts: FeeScheduleCptDiscount[];
  primary_provider: number
  document_url ?: string
}

export interface FeeScheduleCptRate {
  cpt_code: string;
  normal_rate: number;
  custom_discounted_rate: number;
}

export type FeeScheduleCptDiscount = Record<string, unknown>;

interface FeeScheduleStoreState {
    loading: boolean;
    feeScheduleLoadingStatus: string;
    error: {
        error: boolean,
        message: string
    },
    activeProviderId: number
    ui: {
        feeSchedule: FeeSchedule | null
    }
}

export const FeeScheduleStore = signalStore(
    {providedIn: 'root'},
    withEntities<FeeSchedule>(),
    withState<FeeScheduleStoreState>({
        feeScheduleLoadingStatus: 'idle',
        loading: false,
        error: {
            error: false,
            message: ''
        },
        activeProviderId: 0,
        ui: {
            feeSchedule: null
        }
    }),
    withComputed((store) => ({
        feeSchedule: computed(() => store.ui.feeSchedule())
    })),  
    withProps(() => ({
        feeScheduleService: inject(FeeScheduleService),
        userFacade: inject(UserFacade)
    })),
    withMethods((store) => {
        const userFacade = inject(UserFacade)
        const feeScheduleService = inject(FeeScheduleService)
        return {
            setFeeScheduleLoadingStatus(feeScheduleLoadingStatus: string) {
                patchState(store, { 
                    feeScheduleLoadingStatus
                })
            },
            clear() {
                patchState(store, {
                    entityMap: {},
                    ids: [],
                });
                patchState(store, {
                    loading: false,
                    error: {
                        error: false,
                        message: ''
                    },
                    ui: {
                        feeSchedule: null
                    }
                })
            },
            clearError() {
                patchState(store, {
                    error: {
                        error: false,
                        message: ''
                    }
                })
            },
            setError(error: boolean, message: string) {
                patchState(store, {
                    error: {
                        error, message
                    }
                })
            },
            setLoading(loading: boolean) {
                patchState(store, { loading })
            },
            setActiveProviderId(activeProviderId: number) {
                patchState(store, { activeProviderId })
            },
            setFeeSchedule(feeSchedule: FeeSchedule) {
                patchState(store, {
                    ui: {
                        feeSchedule
                    }
                })
            },
            async getFeeScheduleById(provider_id: number, callback ?: () => void, forceRefresh = false, clinic_id?: number ) {
                this.setLoading(true)
                this.clearError()
                const clinicId = Number(clinic_id ?? userFacade.clinic_id())

                const exists = Object.values(store.entityMap()).find(
                    (fs : FeeSchedule) => 
                        fs.clinic_id === clinicId &&
                        fs.provider_id === provider_id
                )

                if(exists && !forceRefresh) {
                    this.setFeeSchedule(exists)
                    this.setLoading(false)
                    callback?.()
                    return
                }

                try {
                    const feeSchedule: FeeSchedule = await firstValueFrom(
                        feeScheduleService.get(clinicId, provider_id).pipe(
                            map((response: any) => response)
                        )
                    )

                    const _feeSchedule = {...feeSchedule, clinic_id: clinicId, provider_id}

                    patchState(store, setEntity(_feeSchedule))
                    this.setFeeSchedule(feeSchedule)
                    callback?.()
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)   
                }
            },
            async submitFeeSchedule(
                clinic_id: number,
                provider_id: number,
                body: any,
                callback?: () => void
            ) {
                this.setLoading(true)
                this.setFeeScheduleLoadingStatus('loading')
                this.clearError()
        
                try {
                    const response = await firstValueFrom(
                        feeScheduleService.save(clinic_id, provider_id, body).pipe(
                            map((res: any) => res)
                        )
                    );

                    const _feeSchedule = {...response, clinic_id: userFacade.clinic_id(), provider_id}
        
                    const feeSchedules = store.entities().filter((feeSchedule) =>
                        feeSchedule.clinic_id !== userFacade.clinic_id() ||
                        feeSchedule.provider_id !== provider_id
                    )

                    patchState(store, setAllEntities(feeSchedules))
                    patchState(store, setEntity(_feeSchedule))
                    this.setFeeSchedule(_feeSchedule)
                    this.setFeeScheduleLoadingStatus('success')
                    callback?.()
                } catch (err) {
                    this.setError(true, extractErrorMessage(err))
                    this.setFeeScheduleLoadingStatus('error')
                } finally {
                    this.setLoading(false)   
                }
            },
    }}),
    withMethods((store) => ({
        getFeeSchedule: rxMethod<{clinic_id: number, provider_id: number}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setFeeScheduleLoadingStatus('loading')
                }),
                exhaustMap(({clinic_id, provider_id}) =>
                    store.feeScheduleService.get(clinic_id, provider_id).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const feeSchedule = {...response, clinic_id, provider_id}
                                patchState(store, setEntity(feeSchedule))
                                store.setFeeSchedule(feeSchedule)
                                store.setFeeScheduleLoadingStatus('success')
                            },
                            error: (error: unknown) => {
                                store.setError(true, extractErrorMessage(error))
                                store.setFeeScheduleLoadingStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        getFeeScheduleLink: rxMethod<{clinic_id: number, provider_id: number, onSuccess?: (value?: any) => void}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setFeeScheduleLoadingStatus("loading")
                }),
                exhaustMap(({clinic_id, provider_id, onSuccess}) => 
                    store.feeScheduleService.getFeeScheduleUrl(clinic_id, provider_id).pipe(
                        tapResponse({
                            next: (response: any) => {
                                onSuccess?.(response.presigned_url)
                            },
                            error: (error: unknown) => {

                            }
                        })
                    )  
                )      
            )
        )
    })),
    withMethods((store) => ({
        revertFeeSchedule: rxMethod<any>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setFeeScheduleLoadingStatus('loading')
                }),
                exhaustMap(({id, clinic_id, provider_id}) => 
                    store.feeScheduleService.delete(clinic_id, provider_id).pipe(
                        tapResponse({
                            next: () => {
                                const feeSchedules = store.entities().filter((f) => f.id !== id)
                                patchState(store, setAllEntities(feeSchedules))
                                store.getFeeSchedule({clinic_id, provider_id})
                            },
                            error: (error: unknown) => {
                                store.setError(true, extractErrorMessage(error))
                                store.setFeeScheduleLoadingStatus('error')
                            }
                        })
                    )
                )
            )
        )
    }))
    
)
